import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import SteckLogo from "./SteckLogo";
import { NAV } from "../constants/testIds";

const LINKS = [
  { to: "/",                       end: true,  label: "Home",              testId: "nav-home"     },
  { to: "/aircraft/beechjet-400a", end: false, label: "Aircraft",          testId: "nav-aircraft" },
  { to: "/about",                  end: false, label: "Management",         testId: "nav-about"       },
  { to: "/fbo-services",           end: false, label: "FBO Services",       testId: "nav-fbo"         },
  { to: "/aircraft-maintenance",   end: false, label: "Maintenance",         testId: "nav-maintenance" },
  { to: "/library",                end: false, label: "Research",           testId: NAV.library       },
  { to: "/operating-outlook",      end: false, label: "Operating Outlook", testId: "nav-outlook"  },
  { to: "/admin",                  end: false, label: "Admin",             testId: NAV.admin      },
];

const W = 16;

function getActiveLink(pathname) {
  return (
    LINKS.filter((l) => !l.end).find((l) => pathname.startsWith(l.to)) ||
    LINKS.find((l)  =>  l.end  && pathname === l.to) ||
    null
  );
}

export default function Navbar() {
  const [open, setOpen]         = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location                = useLocation();

  // True only on the first render after a vestibule choice—flag is cleared immediately
  const [showCandle] = useState(() => {
    try {
      const hit = sessionStorage.getItem("steck_from_vestibule") === "1";
      if (hit) sessionStorage.removeItem("steck_from_vestibule");
      return hit;
    } catch { return false; }
  });

  // Indicator position owned by React state—immune to re-render resets
  const [ind, setInd] = useState({
    left: 0, width: W, opacity: 0, transition: "none",
  });

  const navRef     = useRef(null);
  const indRef     = useRef(null);   // only used for box-shadow RAF
  const glowRaf    = useRef(null);
  const raf1       = useRef(null);
  const raf2       = useRef(null);
  const phaseTimer = useRef(null);
  const prevCenter = useRef(null);

  /* ── Organic warm glow—only touches box-shadow, React never resets it ── */
  useEffect(() => {
    const el = indRef.current;
    if (!el) return;
    const tick = (ms) => {
      const t = ms / 1000;
      const n =
        Math.sin(t * 0.41) * 0.35 +
        Math.sin(t * 0.79) * 0.28 +
        Math.sin(t * 1.53) * 0.20 +
        Math.sin(t * 2.37) * 0.12;
      const v = (n + 0.95) / 1.90;
      const c = (0.48 + v * 0.18).toFixed(3);
      const m = (0.20 + v * 0.15).toFixed(3);
      const o = (0.04 + v * 0.09).toFixed(3);
      el.style.boxShadow = [
        `0 0 3px 1px rgba(255,162,0,${c})`,
        `0 0 8px 2px rgba(255,118,0,${m})`,
        `0 0 18px 6px rgba(255,80,0,${o})`,
      ].join(", ");
      glowRaf.current = requestAnimationFrame(tick);
    };
    glowRaf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(glowRaf.current);
  }, []);

  /* ── Slide indicator to active tab on every route change ── */
  useEffect(() => {
    clearTimeout(phaseTimer.current);
    cancelAnimationFrame(raf1.current);
    cancelAnimationFrame(raf2.current);

    raf1.current = requestAnimationFrame(() => {
      raf2.current = requestAnimationFrame(() => {
        const nav = navRef.current;
        if (!nav) return;

        const active = getActiveLink(location.pathname);
        if (!active) { setInd((s) => ({ ...s, opacity: 0 })); return; }

        const itemEl = nav.querySelector(`[data-route="${active.to}"]`);
        if (!itemEl) return;

        const navRect  = nav.getBoundingClientRect();
        const itemRect = itemEl.getBoundingClientRect();
        const tCenter  = itemRect.left - navRect.left + itemRect.width / 2;
        const tLeft    = tCenter - W / 2;

        // First placement—snap with no animation
        if (prevCenter.current === null) {
          setInd({ left: tLeft, width: W, opacity: 1, transition: "none" });
          prevCenter.current = tCenter;
          return;
        }

        const pCenter = prevCenter.current;
        if (Math.abs(pCenter - tCenter) < 2) return;

        // Phase 1: blob stretches across the gap
        const sLeft  = Math.min(pCenter, tCenter) - W / 2;
        const sWidth = Math.abs(tCenter - pCenter) + W;
        setInd({
          left: sLeft, width: sWidth, opacity: 1,
          transition: "left 180ms ease-in-out, width 180ms ease-in-out",
        });

        // Phase 2: contract into new position with spring
        phaseTimer.current = setTimeout(() => {
          setInd({
            left: tLeft, width: W, opacity: 1,
            transition: "left 320ms cubic-bezier(0.34,1.22,0.64,1), width 320ms cubic-bezier(0.34,1.22,0.64,1)",
          });
        }, 185);

        prevCenter.current = tCenter;
      });
    });

    return () => {
      clearTimeout(phaseTimer.current);
      cancelAnimationFrame(raf1.current);
      cancelAnimationFrame(raf2.current);
    };
  }, [location.pathname]);

  /* ── Reposition on resize ── */
  useEffect(() => {
    const onResize = () => {
      const nav = navRef.current;
      if (!nav) return;
      const active = getActiveLink(location.pathname);
      if (!active) return;
      const itemEl = nav.querySelector(`[data-route="${active.to}"]`);
      if (!itemEl) return;
      const navRect  = nav.getBoundingClientRect();
      const itemRect = itemEl.getBoundingClientRect();
      const tCenter  = itemRect.left - navRect.left + itemRect.width / 2;
      prevCenter.current = tCenter;
      setInd({ left: tCenter - W / 2, width: W, opacity: 1, transition: "none" });
    };
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, [location.pathname]);

  /* ── Scroll header ── */
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const linkClass = ({ isActive }) =>
    `text-[14px] font-medium tracking-tight transition-colors duration-300 ${
      isActive ? "text-[var(--white)]" : "text-[var(--ivory)] hover:text-[var(--blue-soft)]"
    }`;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 backdrop-blur-xl${showCandle ? " nav-candle-header" : ""}`}
      style={{
        background:   scrolled ? "rgba(5,28,44,0.92)" : "rgba(5,28,44,0.55)",
        borderBottom: `1px solid ${scrolled ? "rgba(255,255,255,0.06)" : "transparent"}`,
        transition: "background 300ms, border-color 300ms",
      }}
    >
      <style>{`
        @keyframes candle-overlay {
          0%   { opacity: 0; }
          28%  { opacity: 0.55; }
          52%  { opacity: 0.68; }
          76%  { opacity: 0.40; }
          100% { opacity: 0; }
        }
        @keyframes candle-outer {
          0%   { box-shadow: none; }
          28%  { box-shadow: inset 0 0 48px 10px rgba(255,118,0,0.07), 0 0 28px 7px rgba(255,92,0,0.07), 0 16px 44px 10px rgba(200,54,0,0.03); }
          52%  { box-shadow: inset 0 0 60px 14px rgba(255,126,0,0.09), 0 0 34px 9px rgba(255,98,0,0.08), 0 20px 56px 13px rgba(204,58,0,0.04); }
          76%  { box-shadow: inset 0 0 38px 8px rgba(255,110,0,0.05), 0 0 20px 5px rgba(255,80,0,0.05), 0 12px 34px 7px rgba(194,50,0,0.02); }
          100% { box-shadow: none; }
        }
        .nav-candle-header  { animation: candle-outer 3.5s ease-in-out forwards; }
        .nav-candle-overlay { animation: candle-overlay 3.5s ease-in-out forwards; }
      `}</style>

      {/* Campfire glow—only on first landing from vestibule */}
      {showCandle && (
        <div aria-hidden className="nav-candle-overlay" style={{
          position: "absolute",
          top: 0, bottom: "-55px", left: 0, right: 0,
          pointerEvents: "none", zIndex: 0,
          background: "radial-gradient(ellipse 75% 90% at 58% 0%, rgba(255,122,0,0.15) 0%, rgba(255,85,0,0.07) 42%, transparent 72%)",
        }} />
      )}

      <div className="max-w-[1500px] mx-auto px-6 lg:px-12 h-[88px] flex items-center justify-between" style={{ position: "relative", zIndex: 1 }}>
        <Link to="/" data-testid={NAV.brand} className="flex items-center">
          <SteckLogo size={68} />
        </Link>

        <nav ref={navRef} className="hidden lg:flex items-center gap-5 xl:gap-7 relative pb-[14px]">
          {/* Single persistent WCA light—position owned by React state */}
          <span
            ref={indRef}
            aria-hidden
            style={{
              position:      "absolute",
              bottom:        0,
              left:          ind.left,
              width:         ind.width,
              height:        3,
              borderRadius:  2,
              background:    "linear-gradient(90deg,#FF9000,#FFC800,#FF9000)",
              opacity:       ind.opacity,
              transition:    ind.transition,
              pointerEvents: "none",
              willChange:    "left,width,box-shadow",
            }}
          />

          {LINKS.map(({ to, end, label, testId }) => (
            <span key={to} data-route={to} className="inline-block">
              <NavLink to={to} end={end} data-testid={testId} className={linkClass}>
                {label}
              </NavLink>
            </span>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link to="/about#inquiry" className="btn-primary"
            style={{ padding: "0.65rem 1.3rem", fontSize: "0.78rem" }}>
            Speak with us
          </Link>
        </div>

        <button className="lg:hidden text-[var(--ivory)]"
          onClick={() => setOpen((s) => !s)}
          data-testid={NAV.mobileToggle} aria-label="Toggle menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/5 px-6 py-6 space-y-5"
          style={{ background: "rgba(5,28,44,0.96)" }}>
          {LINKS.map(({ to, end, label }) => (
            <NavLink key={to} to={to} end={end}
              onClick={() => setOpen(false)}
              className="block relative pl-5 group">
              {({ isActive }) => (
                <>
                  <span aria-hidden style={{
                    position: "absolute", left: 0, top: "50%",
                    transform: "translateY(-50%)", width: 2, borderRadius: 2,
                    background: "linear-gradient(180deg,#FF9000,#FFC800,#FF9000)",
                    height: isActive ? 16 : 0, opacity: isActive ? 1 : 0,
                    transition: "height 350ms ease, opacity 350ms ease",
                    boxShadow: isActive
                      ? "0 0 5px 1px rgba(255,150,0,0.65),0 0 10px 2px rgba(255,100,0,0.28)"
                      : "none",
                  }} />
                  <span className={`text-[14px] font-medium tracking-tight transition-colors duration-300 ${
                    isActive ? "text-[var(--white)]" : "text-[var(--ivory)] group-hover:text-[var(--blue-soft)]"
                  }`}>{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      )}
    </header>
  );
}
